import { useTheme } from '@material-ui/core/styles'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Joyride, { ACTIONS, CallBackProps, EVENTS, Props as JoyRideProps, STATUS, Step } from 'react-joyride'

import useDeepCompareEffect from 'src/commons/hooks/useDeepCompareEffect'
import { IStepTranslationFunction } from 'src/Joyride/HelpingJoyride/joyride.types'

import { JoyrideTooltip } from './JoyrideTooltip'

const HiddenBeacon = () => <div style={{ display: 'none' }} />

interface IJoyrideWrapperProps extends Omit<JoyRideProps, 'steps'> {
  steps?: string[]
  stepsTranslator: IStepTranslationFunction
}

interface IJoyrideWrapperCtx {
  isRunning: boolean
  run: () => void
}

const JoyRideWrapperCtx = React.createContext<IJoyrideWrapperCtx | null>(null)

/**
 *
 * @returns
 */
export const useJoyrideWrapper = (): IJoyrideWrapperCtx => {
  const ctx = React.useContext(JoyRideWrapperCtx)

  if (!ctx) {
    throw new Error('useJoyride must be in JoyRideWrapper')
  }

  return ctx
}

/**
 *
 * @param param0
 * @returns
 */
export function JoyrideWrapper({ children, styles, steps, ...props }: React.PropsWithChildren<IJoyrideWrapperProps>) {
  const [isRunning, setIsRunning] = useState(false)
  const [stepIndex, setStepIndex] = useState(1)
  const { t } = useTranslation()
  const translatedSteps = props.stepsTranslator(t)

  const theme = useTheme()

  function handleJoyrideCallback(data: CallBackProps) {
    const { status, action, type } = data

    if ([EVENTS.STEP_AFTER.toString(), EVENTS.TARGET_NOT_FOUND.toString()].includes(type)) {
      // Update state to advance the tour
      setStepIndex((index) => index + (action === ACTIONS.PREV ? -1 : 1))
    }

    if ([STATUS.FINISHED.toString(), STATUS.SKIPPED.toString()].includes(status)) {
      // Need to set our running state to false, so we can restart if we click start again.
      setIsRunning(false)
      setStepIndex(0)
    }
  }

  function getSteps(ids: string[]) {
    let values: Step[] = []

    for (const id of ids) {
      if (id in translatedSteps) {
        values = values.concat(translatedSteps[id])
      }
    }

    return values
  }

  const getCtxValue = () => {
    return {
      isRunning,
      run: () => setIsRunning(true),
    }
  }

  useDeepCompareEffect(() => {
    setStepIndex(0)
    setIsRunning(false)
  }, [steps])

  return (
    <JoyRideWrapperCtx.Provider value={getCtxValue()}>
      <Joyride
        beaconComponent={HiddenBeacon}
        locale={{
          back: t('onboarding.ui.back'),
          close: t('onboarding.ui.close'),
          last: t('onboarding.ui.last'),
          next: t('onboarding.ui.next'),
          skip: t('onboarding.ui.skip'),
        }}
        continuous
        stepIndex={stepIndex}
        showProgress
        showSkipButton
        spotlightPadding={5}
        styles={{
          options: {
            ...styles?.options,
            primaryColor: theme.palette.primary.main,
            textColor: theme.palette.text.primary,
          },
        }}
        run={isRunning}
        callback={(data) => handleJoyrideCallback(data)}
        steps={getSteps(steps ?? [])}
        tooltipComponent={JoyrideTooltip}
        {...props}
      />
      {children}
    </JoyRideWrapperCtx.Provider>
  )
}
